/**
 * NAS 프로젝트 자연어 맥락 매칭 SSOT
 * - 교육청/기관명 → 실제 NAS 프로젝트명 확장 (창녕교육청 → 창녕중)
 * - 번호 학교 disambiguation (에코3초 ≠ 에코1초)
 * - BTL/복합 키워드 (장성BTL)
 */

export interface NasMatchEntry {
  aliases: string[];
  nasId: string;
  nasPrefix: string;
  fullName: string;
}

/** 자연어 입력 → 검색어 확장 (기관/교육청 → 현장 프로젝트명) */
export const NAS_CONTEXT_PHRASES: Record<string, string[]> = {
  '창녕교육청': ['창녕중', '창녕'],
  '창녕교육지원청': ['창녕중', '창녕'],
  '평택교육청': ['평택교육지원청', '평택'],
  '평택교육지원청': ['평택'],
  '양산교육청': ['양산시농업인스마트교육관', '양산'],
  '강남교육청': ['캠코', '강남통합청사'],
};

/**
 * NAS 스캔 전/누락 시에도 매칭되도록 보강하는 정적 맥락 규칙
 * (동적 스캔 결과와 nasId 기준 merge)
 */
export const NAS_CONTEXT_OVERRIDES: NasMatchEntry[] = [
  {
    nasId: 'ID_2603_육군 장성 교육시설_BTL',
    nasPrefix: 'ID_2603',
    fullName: '육군 장성 교육시설 BTL',
    aliases: ['장성btl', '장성BTL', '장성 BTL', '육군장성', '육군 장성', '장성교육', '장성모듈', '장성컬러', 'btl장성'],
  },
  {
    nasId: 'ID_2610_에코3초',
    nasPrefix: 'ID_2610',
    fullName: '에코3초등학교',
    aliases: ['에코3초', '에코3', '2610에코'],
  },
  {
    nasId: 'ID_2540_에코1초',
    nasPrefix: 'ID_2540',
    fullName: '에코1초등학교',
    aliases: ['에코1초', '에코1', '에코초등', '2540에코'],
  },
  {
    nasId: 'ID_2539_장안1초',
    nasPrefix: 'ID_2539',
    fullName: '장안1초등학교',
    aliases: ['장안1초', '장안1', '장안1초등학교'],
  },
  {
    nasPrefix: 'ID_2506',
    nasId: 'ID_2506_평택교육지원청',
    fullName: '평택교육지원청',
    aliases: ['평택', '평택교육', '컨퍼런스홀', '평택인테리어'],
  },
];

/** 짧은 토큰 → nasPrefix (맥락 키워드). 번호 학교는 여기 넣지 않음 */
export const NAS_KEYWORD_PREFIX_MAP: Record<string, string> = {
  '장성': 'ID_2603',
  '육군': 'ID_2603',
  '병영': 'ID_2603',
  'btl': 'ID_2603',
  '광교': 'ID_2604',
  '중흥': 'ID_2604',
  '갈매': 'ID_2605',
  '감매': 'ID_2605',
  '평택': 'ID_2506',
  '웅촌': 'ID_2543',
  '함양': 'ID_2514',
  '분당': 'ID_2552',
  '중봉': 'ID_2551',
  '태릉': 'ID_2430',
  '태능': 'ID_2430',
  '밀양': 'ID_2532',
  '벡스코': 'ID_2554',
};

const SCHOOL_NUM_RE = /(\d)\s*(?:초|중|고)/g;

export function normalizeNasText(text: string): string {
  return text.replace(/[■*\s#]+/g, '').toLowerCase();
}

/** 텍스트에서 학교 번호 추출 (에코3초 → 3) */
export function extractSchoolNumbers(text: string): number[] {
  const nums = new Set<number>();
  const normalized = text.replace(/\s/g, '');
  let m: RegExpExecArray | null;
  const re = new RegExp(SCHOOL_NUM_RE.source, 'g');
  while ((m = re.exec(normalized)) !== null) {
    nums.add(parseInt(m[1], 10));
  }
  // ID_2610 패턴
  const idM = normalized.match(/id_(\d{4})/i);
  if (idM) {
    const suffix = idM[1].slice(-1);
    if (/[1-9]/.test(suffix)) nums.add(parseInt(suffix, 10));
  }
  return [...nums];
}

/** 프로젝트 항목에서 대표 학교 번호 추출 */
export function extractEntrySchoolNumber(entry: NasMatchEntry): number | null {
  const src = `${entry.nasId}${entry.fullName}${entry.aliases.join('')}`;
  const m = src.match(/(\d)\s*(?:초|중|고)/);
  if (m) return parseInt(m[1], 10);
  const idM = entry.nasPrefix.match(/ID_(\d{4})/);
  if (idM) {
    const last = idM[1].slice(-1);
    if (/[0-9]/.test(last) && entry.fullName.match(/\d(?:초|중|고)/)) {
      return parseInt(last, 10);
    }
  }
  return null;
}

export function expandSearchText(text: string): string {
  let expanded = text;
  const lower = text.toLowerCase();
  for (const [phrase, additions] of Object.entries(NAS_CONTEXT_PHRASES)) {
    if (lower.includes(phrase.toLowerCase().replace(/\s/g, '')) ||
        text.includes(phrase)) {
      expanded += ` ${additions.join(' ')}`;
    }
  }
  return expanded;
}

/** 번호 학교 disambiguation — 에코3초 입력 시 에코1초 후보 제거 */
export function isNumberCompatible(text: string, alias: string, entry: NasMatchEntry): boolean {
  const textNums = extractSchoolNumbers(text);
  const entryNum = extractEntrySchoolNumber(entry);
  const aliasNorm = alias.replace(/\s/g, '');
  const aliasNumM = aliasNorm.match(/(\d)(?:초|중|고)?$/);

  if (textNums.length > 0) {
    const required = textNums[0];
    if (entryNum !== null && entryNum !== required) return false;
    if (aliasNumM && parseInt(aliasNumM[1], 10) !== required) return false;
    return true;
  }

  // 텍스트에 번호 없음 + 번호 있는 학교 프로젝트 + 짧은/무번호 alias → 모호 → 제외
  if (entryNum !== null && !aliasNumM && aliasNorm.length <= 5) {
    return false;
  }

  return true;
}

export function mergeNasEntries(
  dynamic: NasMatchEntry[],
  staticFallback: NasMatchEntry[] = []
): NasMatchEntry[] {
  const map = new Map<string, NasMatchEntry>();
  for (const e of staticFallback) map.set(e.nasId, e);
  for (const e of NAS_CONTEXT_OVERRIDES) {
    const prev = map.get(e.nasId);
    if (prev) {
      map.set(e.nasId, {
        ...prev,
        aliases: [...new Set([...prev.aliases, ...e.aliases])],
      });
    } else {
      map.set(e.nasId, e);
    }
  }
  for (const e of dynamic) {
    const prev = map.get(e.nasId);
    if (prev) {
      map.set(e.nasId, {
        ...e,
        aliases: [...new Set([...prev.aliases, ...e.aliases])],
      });
    } else {
      map.set(e.nasId, e);
    }
  }
  return [...map.values()];
}

export function enrichNasProjectAliases(
  nasId: string,
  fullName: string,
  baseAliases: string[]
): string[] {
  const aliases = new Set(baseAliases.map(a => a.toLowerCase()));
  aliases.add(nasId.toLowerCase());
  aliases.add(fullName.toLowerCase());

  for (const override of NAS_CONTEXT_OVERRIDES) {
    if (override.nasId === nasId || override.nasPrefix && nasId.startsWith(override.nasPrefix)) {
      override.aliases.forEach(a => aliases.add(a.toLowerCase()));
    }
  }

  for (const [phrase, expansions] of Object.entries(NAS_CONTEXT_PHRASES)) {
    if (fullName.includes(phrase) || expansions.some(e => fullName.includes(e))) {
      expansions.forEach(e => aliases.add(e.toLowerCase()));
      aliases.add(phrase.toLowerCase());
    }
  }

  const koreanWords = fullName.match(/[가-힣]{2,}/g) || [];
  koreanWords.forEach(w => aliases.add(w.toLowerCase()));

  return [...aliases];
}

export interface NasResolveResult {
  entry: NasMatchEntry;
  matchedAlias: string;
  layer: 'exact' | 'keyword' | 'context';
}

/**
 * 일정 제목/프로젝트명 → NAS 프로젝트 매칭 (동기)
 * 긴 alias 우선 + 번호 학교 disambiguation
 */
export function resolveNasProjectFromText(
  title: string,
  projectName: string,
  entries: NasMatchEntry[]
): NasResolveResult | null {
  const expanded = expandSearchText(`${title} ${projectName}`);
  const normalized = normalizeNasText(expanded);

  type Candidate = { entry: NasMatchEntry; alias: string; len: number };
  const candidates: Candidate[] = [];

  for (const entry of entries) {
    for (const alias of entry.aliases) {
      const aliasNorm = normalizeNasText(alias);
      if (aliasNorm.length < 2) continue;
      if (!normalized.includes(aliasNorm)) continue;
      if (!isNumberCompatible(expanded, alias, entry)) continue;
      candidates.push({ entry, alias, len: aliasNorm.length });
    }
  }

  if (candidates.length > 0) {
    candidates.sort((a, b) => b.len - a.len);
    const best = candidates[0];
    return { entry: best.entry, matchedAlias: best.alias, layer: 'exact' };
  }

  // 키워드 → prefix (BTL 등 복합어 우선)
  const combined = `${title} ${projectName}`.toLowerCase();
  if (/장성.*btl|btl.*장성|장성btl/.test(combined.replace(/\s/g, ''))) {
    const entry = entries.find(e => e.nasPrefix === 'ID_2603');
    if (entry) return { entry, matchedAlias: '장성BTL', layer: 'context' };
  }

  const tokens = expanded.match(/[가-힣a-zA-Z0-9]{2,}/g) || [];
  const sortedTokens = [...tokens].sort((a, b) => b.length - a.length);
  for (const token of sortedTokens) {
    const prefix = NAS_KEYWORD_PREFIX_MAP[token];
    if (!prefix) continue;
    const entry = entries.find(e => e.nasPrefix === prefix);
    if (!entry) continue;
    if (!isNumberCompatible(expanded, token, entry)) continue;
    return { entry, matchedAlias: token, layer: 'keyword' };
  }

  return null;
}
